import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Chip,
    Divider,
    Stack,
    Typography
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { format } from 'date-fns';
import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { Reward } from '@api/Rewards/Reward';
import Counter from '@components/Utilities/Counter';
import { currencyFormatter } from '@utils/Numbers';

interface OrderSummaryCardProps {
    order: Order;
    /** La tasa de puntos: con ella se muestran los pts aplicados junto al importe. */
    reward?: Reward;
}

/**
 * La tarjeta principal del detalle de `/orders`: número, estado, canal, cliente,
 * total y el acordeón "Detalles del pedido" (fechas, desglose y cómo se pagó).
 * Cómo se pagó se lee SOLO con `getOrderPaidBy` (entiende `payments[]` y los
 * escalares viejos).
 */
const OrderSummaryCard: React.FunctionComponent<OrderSummaryCardProps> = ({ order, reward }) => (
    <Stack component={Card} borderRadius={2} elevation={3}>
        <Stack p={3} spacing={2}>
            {/* Línea 1: Número, Estado y Tipo de Entrega */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box bgcolor="lightgrey" px={2} py={1} borderRadius={2}>
                    <Typography variant="h6" fontWeight={600}>
                        {`#${String(Number(order.shortId.split('-')[1])).padStart(2, '0')}`}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    {order.isReservation && <Chip label="Reserva" color="success" size="small" />}
                    {order.deliveryType && (
                        <Chip
                            label={order.deliveryType === DeliveryType.Delivery ? 'Delivery' : 'Pickup'}
                            // `secondary` es #FFFFFF en este tema: el chip de Pickup salía blanco sobre blanco.
                            color={order.deliveryType === DeliveryType.Delivery ? 'primary' : 'default'}
                            size="small"
                        />
                    )}
                    <Chip
                        label={order.status}
                        color={
                            order.status === OrderStatus.Completed
                                ? 'success'
                                : order.status === OrderStatus.Cancelled
                                ? 'error'
                                : 'warning'
                        }
                    />
                </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary">
                {order.id}
            </Typography>

            <Stack>
                <Typography variant="body2" color="text.secondary">
                    Pedido de
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    {order.salesChannel && (
                        <Typography variant="subtitle1" fontWeight={600}>
                            {order.salesChannel.name}
                        </Typography>
                    )}
                    <Typography variant="subtitle1" fontWeight={600}>
                        {order.customer?.profile?.name}
                    </Typography>
                    {order.customer.phone && (
                        <Typography variant="subtitle1" color="primary">
                            {order.customer.phone}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {/* Línea 3: Total */}
            <Stack>
                <Typography variant="body2" color="text.secondary">
                    Total
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                    {currencyFormatter(order.total)}
                </Typography>
            </Stack>
        </Stack>

        {/* Acordeón de detalles */}
        <Accordion elevation={0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Detalles del pedido</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            Pedido ID
                        </Typography>
                        <Typography variant="body1">{order.shortId}</Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            Ingresado por
                        </Typography>
                        <Typography variant="body1">
                            {order.collaboratorData?.profile.name || 'No especificado'}
                        </Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            Fecha y hora del pedido
                        </Typography>
                        <Typography variant="body1">{format(order.date.toDate(), 'dd/MM/yyyy HH:mm')}</Typography>
                    </Stack>

                    {order.reservationDate && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Fecha y hora de la reserva
                            </Typography>
                            <Typography variant="body1">
                                {format(order.reservationDate.toDate(), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Stack>
                    )}

                    {order.pickupDate && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Fecha y hora de recogida
                            </Typography>
                            <Typography variant="body1">
                                {format(order.pickupDate.toDate(), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Stack>
                    )}

                    {order.status === OrderStatus.Completed && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Tiempo total
                            </Typography>
                            <Typography variant="body1">
                                <Counter timeInSeconds={order.duration} />
                            </Typography>
                        </Stack>
                    )}

                    <Divider />

                    {/* Resumen financiero */}
                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            Subtotal
                        </Typography>
                        <Typography variant="body1">{currencyFormatter(order.subtotal)}</Typography>
                    </Stack>

                    {order.promotion > 0 && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Descuento
                                {order.promotionalCode && <span> ({order.promotionalCode.code})</span>}
                            </Typography>
                            <Typography variant="body1" color="error.main">
                                -{currencyFormatter(order.promotion)}
                            </Typography>
                        </Stack>
                    )}

                    {order.pointsAmount > 0 && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Puntos aplicados
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {reward?.reward.value && (
                                    <Typography variant="body1">
                                        {(order.pointsAmount * 100) / reward?.reward?.value} pts
                                    </Typography>
                                )}
                                <Typography variant="body1">{currencyFormatter(order.pointsAmount)}</Typography>
                            </Stack>
                        </Stack>
                    )}

                    {order.shippingCost > 0 && (
                        <Stack>
                            <Typography variant="body2" color="text.secondary">
                                Costo de envío
                            </Typography>
                            <Typography variant="body1">{currencyFormatter(order.shippingCost)}</Typography>
                        </Stack>
                    )}

                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            Total
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {currencyFormatter(order.total)}
                        </Typography>
                    </Stack>

                    <Divider />

                    {/* Métodos de pago */}
                    <Stack spacing={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Métodos de pago
                        </Typography>

                        {getOrderPaidBy(order, PaymentMethod.Cash) > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Efectivo</Typography>
                                <Typography variant="body2">
                                    {currencyFormatter(getOrderPaidBy(order, PaymentMethod.Cash))}
                                </Typography>
                            </Stack>
                        )}

                        {getOrderPaidBy(order, PaymentMethod.BankTransfer) > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Transferencia</Typography>
                                <Typography variant="body2">
                                    {currencyFormatter(getOrderPaidBy(order, PaymentMethod.BankTransfer))}
                                </Typography>
                            </Stack>
                        )}

                        {getOrderPaidBy(order, PaymentMethod.CreditCard) > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Tarjeta</Typography>
                                <Typography variant="body2">
                                    {currencyFormatter(getOrderPaidBy(order, PaymentMethod.CreditCard))}
                                </Typography>
                            </Stack>
                        )}

                        {order.pointsAmount > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Puntos</Typography>
                                <Typography variant="body2">{currencyFormatter(order.pointsAmount)}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    </Stack>
);

export default OrderSummaryCard;
